import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { interval, Subscription } from 'rxjs';
import { AuthService } from './auth.service';
import { AuthSession } from '../models/profile';

@Injectable({
  providedIn: 'root',
})
export class SessionCountdownService {
  private readonly authService = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly remainingSeconds = signal(0);
  private readonly hasExpiry = signal(false);
  private expired = false;
  private tickSubscription?: Subscription;

  readonly remainingTimeText = computed(() =>
    this.formatRemaining(this.remainingSeconds()),
  );

  constructor() {
    this.syncFromAuth(this.authService.getAuthFromLocalStorage());
    this.authService.currentUser$.subscribe((auth) => {
      this.syncFromAuth(auth ?? undefined);
    });
    this.startTicker();
    this.destroyRef.onDestroy(() => this.tickSubscription?.unsubscribe());
  }

  private syncFromAuth(auth?: AuthSession): void {
    const exp = auth?.payload?.exp;
    if (typeof exp === 'number' && exp > 0) {
      this.hasExpiry.set(true);
      this.expired = false;
      this.remainingSeconds.set(this.calculateRemaining(exp));
      return;
    }

    this.hasExpiry.set(false);
    this.remainingSeconds.set(0);
  }

  private startTicker(): void {
    if (this.tickSubscription) {
      return;
    }

    this.tickSubscription = interval(1000).subscribe(() => {
      this.updateCountdown();
    });
  }

  private updateCountdown(): void {
    if (!this.hasExpiry()) {
      return;
    }

    const exp = this.authService.getAuthFromLocalStorage()?.payload?.exp;
    if (!exp) {
      this.hasExpiry.set(false);
      this.remainingSeconds.set(0);
      return;
    }

    const remaining = this.calculateRemaining(exp);
    this.remainingSeconds.set(remaining);

    if (remaining <= 0 && !this.expired) {
      this.expired = true;
      this.hasExpiry.set(false);
      this.authService.logout();
    }
  }

  private calculateRemaining(exp: number): number {
    const now = Math.floor(Date.now() / 1000);
    return Math.max(0, exp - now);
  }

  private formatRemaining(totalSeconds: number): string {
    const clamped = Math.max(0, totalSeconds);
    const minutes = Math.floor(clamped / 60);
    const seconds = clamped % 60;
    return `${minutes.toString().padStart(2, '0')}m ${seconds
      .toString()
      .padStart(2, '0')}s`;
  }
}
